import { type LucideIcon } from "lucide-react";

import { ThemeProvider } from "@mui/material";

import { ValueContainer } from "@components/landing/Values/ValueContainer";
import { lightTheme } from "@themes";

import { ctaButtonBaseClassName, darkGradient } from "./ctaButtonStyles";

export interface TargetAudienceCopy {
  header: {
    h2: string;
    h3: string;
    p: string;
  };
  points: {
    Icon: LucideIcon;
    title: string;
    description: string;
  }[];
  cta: {
    h4: string;
    text: string;
    href: string;
    buttonText: string;
  };
}

const AuditAnimation = "/animations/audit.json";
const ConsultingAnimation = "/animations/consulting.json";
const DebtAnimation = "/animations/debt.json";
const E2EAnimation = "/animations/e2e.json";
const IterationAnimation = "/animations/iterations.json";
const UXAnimation = "/animations/uxui.json";

const animations = [
  AuditAnimation,
  ConsultingAnimation,
  DebtAnimation,
  E2EAnimation,
  IterationAnimation,
  UXAnimation,
];

export function TargetAudience({ copy }: { copy: TargetAudienceCopy }) {
  return (
    <section id="dla-kogo" className="py-24 bg-background-light">
      <div className="container mx-auto px-6 md:px-12 max-w-7xl">
        <div className="text-center max-w-3xl mx-auto mb-16">
          <h2
            className={`text-sm font-semibold bg-clip-text text-transparent uppercase tracking-widest mb-3`}
            style={{ backgroundImage: darkGradient }}
          >
            {copy.header.h2}
          </h2>
          <h3 className="text-3xl md:text-4xl font-semibold text-slate-900 mb-6">
            {copy.header.h3}
          </h3>
          <p className="text-lg text-slate-600">{copy.header.p}</p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-2">
          {copy.points.map((point, index) => (
            <div key={point.title} className="py-4 px-8">
              <ThemeProvider key={point.title} theme={lightTheme}>
                <ValueContainer
                  key={point.title}
                  icon={animations[index % animations.length]}
                  title={point.title}
                >
                  <p className="text-slate-600 leading-relaxed font-normal">
                    {point.description}
                  </p>
                </ValueContainer>
              </ThemeProvider>
            </div>
          ))}

          <div
            className={`${ctaButtonBaseClassName} rounded-2xl p-8 text-white flex flex-col justify-center items-start`}
          >
            <h4 className="text-2xl font-semibold mb-4 text-gray-700">
              {copy.cta.h4}
            </h4>
            <p className="mb-6 text-gray-700 font-normal">{copy.cta.text}</p>
            <a
              href="#kontakt"
              className="bg-white text-gray-900 font-medium py-3 px-6 rounded-full hover:bg-gray-50 transition-colors"
            >
              {copy.cta.buttonText}
            </a>
          </div>
        </div>
      </div>
    </section>
  );
}
